import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
	fetchVideoTest,
	fetchVideoIdInfo,
	fetchVideoUserId,
	fetchVideoHotDance,
	fetchVideoHashtagId,
	fetchVideoDanceIdBest,
	fetchVideoDanceId,
	fetchVideoArtistId,
	fetchVideoFeed,
	fetchVideoFollower,
	fetchVideoIdDynamiclink,
	fetchVideoIdAlive,
	fetchVideoBestDancer,
	fetchVideoTournamentMatchId,
	fetchVideoTournamentIdBest,
	fetchVideoTournamentChampion,
	fetchVideoTournamentChampionWeb,
	fetchVideoTournamentIdWeb,
	fetchVideoTournamentIdRound,
	fetchVideoInternalIdAlive,
	fetchVideoUserIdTournamentId,
} from './videoAPI';
import {
	VideoController_test_Response,
	VideoController_getVideoInfo_Params,
	VideoController_getVideoInfo_Response,
	VideoController_getUserVideoList_Params,
	VideoController_getUserVideoList_Response,
	VideoController_getHotDanceVideoList_Params,
	VideoController_getHotDanceVideoList_Response,
	VideoController_getHashtagVideoList_Params,
	VideoController_getHashtagVideoList_Response,
	VideoController_getDanceBestVideoList_Params,
	VideoController_getDanceBestVideoList_Response,
	VideoController_getDanceVideoList_Params,
	VideoController_getDanceVideoList_Response,
	VideoController_getArtistVideoList_Params,
	VideoController_getArtistVideoList_Response,
	VideoController_getFeedVideoList_Params,
	VideoController_getFeedVideoList_Response,
	VideoController_getFollowerVideoList_Params,
	VideoController_getFollowerVideoList_Response,
	VideoController_shareVideo_Params,
	VideoController_shareVideo_Response,
	VideoController_checkVideoAlive_Params,
	VideoController_checkVideoAlive_Response,
	VideoController_getBestDancerVideoList_Params,
	VideoController_getBestDancerVideoList_Response,
	VideoController_getTournamentMatchVideoList_Params,
	VideoController_getTournamentMatchVideoList_Response,
	VideoController_getTournamentBestVideoList_Params,
	VideoController_getTournamentBestVideoList_Response,
	VideoController_getTournamentChampionVideoList_Params,
	VideoController_getTournamentChampionVideoList_Response,
	VideoController_getTournamentChampionVideoWebList_Params,
	VideoController_getTournamentChampionVideoWebList_Response,
	VideoController_getTournamentVideoListWeb_Params,
	VideoController_getTournamentVideoListWeb_Response,
	VideoController_getTournamentVideoList_Params,
	VideoController_getTournamentVideoList_Response,
	VideoController_checkVideoAliveInternal_Params,
	VideoController_checkVideoAliveInternal_Response,
	VideoController_getTournamentProfileVideoList_Params,
	VideoController_getTournamentProfileVideoList_Response,
} from '@/domains/content/types/validated';

export const useFetchVideoTestQuery = (enabled?: boolean): UseQueryResult<VideoController_test_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTest'],
		queryFn: () => fetchVideoTest(),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoIdInfoQuery = (
	params: VideoController_getVideoInfo_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getVideoInfo_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoIdInfo', params],
		queryFn: () => fetchVideoIdInfo(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoUserIdQuery = (
	params: VideoController_getUserVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getUserVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoUserId', params],
		queryFn: () => fetchVideoUserId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoHotDanceQuery = (
	params: VideoController_getHotDanceVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getHotDanceVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoHotDance', params],
		queryFn: () => fetchVideoHotDance(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoHashtagIdQuery = (
	params: VideoController_getHashtagVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getHashtagVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoHashtagId', params],
		queryFn: () => fetchVideoHashtagId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoDanceIdBestQuery = (
	params: VideoController_getDanceBestVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getDanceBestVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoDanceIdBest', params],
		queryFn: () => fetchVideoDanceIdBest(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoDanceIdQuery = (
	params: VideoController_getDanceVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getDanceVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoDanceId', params],
		queryFn: () => fetchVideoDanceId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoArtistIdQuery = (
	params: VideoController_getArtistVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getArtistVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoArtistId', params],
		queryFn: () => fetchVideoArtistId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoFeedQuery = (
	params: VideoController_getFeedVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getFeedVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoFeed', params],
		queryFn: () => fetchVideoFeed(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoFollowerQuery = (
	params: VideoController_getFollowerVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getFollowerVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoFollower', params],
		queryFn: () => fetchVideoFollower(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoIdDynamiclinkQuery = (
	params: VideoController_shareVideo_Params,
	enabled?: boolean
): UseQueryResult<VideoController_shareVideo_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoIdDynamiclink', params],
		queryFn: () => fetchVideoIdDynamiclink(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoIdAliveQuery = (
	params: VideoController_checkVideoAlive_Params,
	enabled?: boolean
): UseQueryResult<VideoController_checkVideoAlive_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoIdAlive', params],
		queryFn: () => fetchVideoIdAlive(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoBestDancerQuery = (
	params: VideoController_getBestDancerVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getBestDancerVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoBestDancer', params],
		queryFn: () => fetchVideoBestDancer(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentMatchIdQuery = (
	params: VideoController_getTournamentMatchVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentMatchVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentMatchId', params],
		queryFn: () => fetchVideoTournamentMatchId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentIdBestQuery = (
	params: VideoController_getTournamentBestVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentBestVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentIdBest', params],
		queryFn: () => fetchVideoTournamentIdBest(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentChampionQuery = (
	params: VideoController_getTournamentChampionVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentChampionVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentChampion', params],
		queryFn: () => fetchVideoTournamentChampion(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentChampionWebQuery = (
	params: VideoController_getTournamentChampionVideoWebList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentChampionVideoWebList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentChampionWeb', params],
		queryFn: () => fetchVideoTournamentChampionWeb(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentIdWebQuery = (
	params: VideoController_getTournamentVideoListWeb_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentVideoListWeb_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentIdWeb', params],
		queryFn: () => fetchVideoTournamentIdWeb(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoTournamentIdRoundQuery = (
	params: VideoController_getTournamentVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoTournamentIdRound', params],
		queryFn: () => fetchVideoTournamentIdRound(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoInternalIdAliveQuery = (
	params: VideoController_checkVideoAliveInternal_Params,
	enabled?: boolean
): UseQueryResult<VideoController_checkVideoAliveInternal_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoInternalIdAlive', params],
		queryFn: () => fetchVideoInternalIdAlive(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};

export const useFetchVideoUserIdTournamentIdQuery = (
	params: VideoController_getTournamentProfileVideoList_Params,
	enabled?: boolean
): UseQueryResult<VideoController_getTournamentProfileVideoList_Response, Error> => {
	return useQuery({
		queryKey: ['fetchVideoUserIdTournamentId', params],
		queryFn: () => fetchVideoUserIdTournamentId(params),
		enabled: enabled,
		throwOnError: (error: Error) => {
			console.error('[Query Error]', error);
			toast.error(error.message || 'Query failed');
			return true;
		},
	});
};
